import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  Trash2,
  Image,
  Upload,
} from "lucide-react";
import { adminApi, isAdminLoggedIn, verifyAdminToken } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) {
    return `${API}${url}`;
  }
  return url;
};

export default function AdminSettings() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [newImageCaption, setNewImageCaption] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
        return;
      }
      fetchData();
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const galleryRes = await adminApi.get("/gallery");
      setGalleryImages(galleryRes.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      // Upload the file
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await adminApi.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Add to gallery
      const response = await adminApi.post("/gallery", {
        url: uploadRes.data.url,
        caption: newImageCaption.trim(),
      });

      setGalleryImages([...galleryImages, response.data]);
      setNewImageCaption("");
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const deleteGalleryImage = async (imageId) => {
    try {
      await adminApi.delete(`/gallery/${imageId}`);
      setGalleryImages(galleryImages.filter((img) => img.id !== imageId));
      toast.success("Image deleted!");
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white">SETTINGS</h1>
            <p className="text-muted-foreground">Manage gallery images</p>
          </div>
        </div>

        {/* Gallery Management */}
        <Card className="glass border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              Gallery Images
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add New Image */}
            <div className="bg-muted/30 p-4 rounded-lg space-y-4">
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Upload New Image</p>
              <div className="flex flex-col md:flex-row gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="flex-1 bg-muted/50 border-transparent hover:bg-muted/70"
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Choose Image File"}
                </Button>
                <Input
                  placeholder="Caption (optional)"
                  value={newImageCaption}
                  onChange={(e) => setNewImageCaption(e.target.value)}
                  className="flex-1 bg-muted/50 border-transparent"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Supported formats: JPEG, PNG, GIF, WebP. Maximum file size: 5MB.
              </p>
            </div>
            {/* Current Images */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryImages.length === 0 ? (
                <p className="col-span-full text-center text-muted-foreground py-8">
                  No gallery images yet. Add some above!
                </p>
              ) : (
                galleryImages.map((img) => (
                  <motion.div
                    key={img.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative group"
                  >
                    <div className="aspect-square rounded-lg overflow-hidden">
                      <img
                        src={getImageUrl(img.url)}
                        alt={img.caption || "Gallery"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => deleteGalleryImage(img.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {img.caption && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{img.caption}</p>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
